/**
 * Installation license → which add-on controls the apps show (docs/licensing-design.md).
 *
 * admin-service answers GET /v1/license/features with { available, locked, hidden }:
 *   - hidden:    the license never included the feature (not bought): don't show the control
 *   - locked:    bought, but blocked now (license restricted): show it disabled with a lock
 *                and "Not included in your current licence, contact your administrator"
 *   - available: everything else (also the 60-day window without a license)
 *
 * Fails open like the services do: no answer (yet), an error or an unknown feature all
 * count as available. The services still refuse with 403 LICENSE_FEATURE; this only
 * shapes the UI.
 */
import { create } from 'zustand';
import { api, API_BASE } from './api';

export type LicenseFeature = 'ai' | 'transcription' | 'office' | 'mail' | 'bridge';
export type FeatureState = 'available' | 'locked' | 'hidden';

export interface FeatureAccess {
  available: string[];
  locked: string[];
  hidden: string[];
}

/** Refresh interval while signed in. */
export const LICENSE_REFRESH_MS = 5 * 60_000;

/** One feature's state; null access (unknown) is available. */
export function classifyFeature(access: FeatureAccess | null, feature: LicenseFeature): FeatureState {
  if (!access) return 'available';
  if (access.hidden.includes(feature)) return 'hidden';
  if (access.locked.includes(feature)) return 'locked';
  return 'available';
}

/**
 * A control that needs several features (mail writing help needs 'ai' and 'mail'):
 * hidden if any is hidden, else locked if any is locked, else available.
 */
export function classifyFeatures(access: FeatureAccess | null, features: readonly LicenseFeature[]): FeatureState {
  const states = features.map((f) => classifyFeature(access, f));
  if (states.includes('hidden')) return 'hidden';
  if (states.includes('locked')) return 'locked';
  return 'available';
}

function isAccess(x: unknown): x is FeatureAccess {
  const a = x as Partial<FeatureAccess> | null;
  return !!a && Array.isArray(a.available) && Array.isArray(a.locked) && Array.isArray(a.hidden);
}

interface LicenseFeaturesState {
  /** null: not known (not loaded, or the last load failed) → everything available. */
  access: FeatureAccess | null;
  load: () => Promise<void>;
  reset: () => void;
}

export const useLicenseStore = create<LicenseFeaturesState>((set) => ({
  access: null,
  load: async () => {
    try {
      // Plain fetch, not api.get: a 401 here must never sign the user out
      const token = await api.freshToken();
      if (!token) { set({ access: null }); return; }
      const res = await fetch(`${API_BASE}/v1/license/features`, { headers: { Authorization: `Bearer ${token}` } });
      const body = res.ok ? await res.json() as { success?: boolean; data?: unknown } : null;
      set({ access: body?.success && isAccess(body.data) ? body.data : null });
    } catch {
      set({ access: null }); // fail open
    }
  },
  reset: () => set({ access: null }),
}));
